import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined, ExceptionOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import {
  getQualityNcrCapaQueue,
  type InspectionQueueRow,
  type NcrCapaQueueMode,
  type OpenNCRRow,
  type OverdueCAPARow,
  type QmsAgingBucket,
  type QualityDashboardActionSummary,
} from '@/services/qualityDashboard.service';

const { Text } = Typography;

const agingColor: Record<QmsAgingBucket, string> = {
  '0-7': 'green',
  '8-14': 'gold',
  '15-30': 'orange',
  '30+': 'red',
};

export const QmsNcrCapaFollowUpZone: React.FC<{
  summary: QualityDashboardActionSummary;
  asOnDate?: string;
  initialMode?: NcrCapaQueueMode;
}> = ({ summary, asOnDate, initialMode = 'open_ncr' }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<NcrCapaQueueMode>(initialMode);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [aging, setAging] = useState<QmsAgingBucket | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setMode(initialMode);
    setPage(1);
  }, [initialMode]);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['qms-ncr-capa-queue', mode, page, pageSize, aging, searchApplied, asOnDate],
    queryFn: () =>
      getQualityNcrCapaQueue({
        mode,
        page,
        pageSize,
        aging_bucket: aging,
        search: searchApplied || undefined,
        as_on_date: asOnDate,
      }),
    placeholderData: (prev) => prev,
  });

  const openNcr = summary.open_ncr_count ?? 0;
  const overdueCapa = summary.overdue_capa_count ?? 0;
  const overdueInsp = summary.overdue_inspection_count ?? 0;
  const agingCounts =
    data?.aging ??
    (mode === 'open_ncr'
      ? summary.open_ncr_aging
      : mode === 'overdue_capa'
        ? summary.overdue_capa_aging
        : undefined);
  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];

  const ncrColumns: ColumnsType<OpenNCRRow> = [
    {
      title: 'NCR #',
      dataIndex: 'ncr_number',
      key: 'ncr_number',
      width: 140,
      ellipsis: true,
      render: (v: string, r) => <a onClick={() => navigate(r.link_path)}>{v}</a>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 110,
      render: (v: string) => (
        <Tag color={v === 'Critical' ? 'red' : v === 'Major' ? 'orange' : 'gold'}>{v}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Identified',
      dataIndex: 'identified_date',
      key: 'identified_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    {
      title: 'Aging',
      dataIndex: 'aging_days',
      key: 'aging_days',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.aging_days - b.aging_days,
      render: (d: number, r) => {
        const bucket = r.aging_bucket ?? '0-7';
        return (
          <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
            {d}d · {bucket}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          Open
        </Button>
      ),
    },
  ];

  const capaColumns: ColumnsType<OverdueCAPARow> = [
    {
      title: 'CAPA #',
      dataIndex: 'capa_number',
      key: 'capa_number',
      width: 140,
      ellipsis: true,
      render: (v: string, r) => <a onClick={() => navigate(r.link_path)}>{v}</a>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Due',
      dataIndex: 'target_completion_date',
      key: 'target_completion_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue_days',
      key: 'overdue_days',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.overdue_days - b.overdue_days,
      render: (d: number, r) => {
        const bucket = r.aging_bucket ?? '0-7';
        return (
          <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
            {d}d · {bucket}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          Open
        </Button>
      ),
    },
  ];

  const inspColumns: ColumnsType<InspectionQueueRow> = [
    {
      title: 'Inspection #',
      dataIndex: 'inspection_number',
      key: 'inspection_number',
      width: 160,
      ellipsis: true,
      render: (v: string, r) => <a onClick={() => navigate(r.link_path)}>{v}</a>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Scheduled',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 110,
      render: (v?: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue_days',
      key: 'overdue_days',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.overdue_days ?? 0) - (b.overdue_days ?? 0),
      render: (d?: number, r?: InspectionQueueRow) => {
        const days = d ?? 0;
        const bucket = r?.aging_bucket ?? '0-7';
        return (
          <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
            {days}d · {bucket}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          Complete
        </Button>
      ),
    },
  ];

  const columns =
    mode === 'open_ncr' ? ncrColumns : mode === 'overdue_capa' ? capaColumns : inspColumns;

  const tableBody = isError ? (
    <Alert
      type="error"
      showIcon
      message={(error as Error)?.message ?? 'Failed to load NCR / CAPA / inspection queue'}
    />
  ) : isFetching && !data ? (
    <Spin />
  ) : rows.length === 0 ? (
    <EmptyQueueState message="No items in this queue for the selected filters." />
  ) : (
    <Table
      rowKey="id"
      size="small"
      tableLayout="fixed"
      loading={isFetching}
      dataSource={rows as any}
      columns={columns as any}
      scroll={{ x: 800 }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
        onChange: (nextPage, nextSize) => {
          setPage(nextPage);
          if (nextSize && nextSize !== pageSize) {
            setPageSize(nextSize);
            setPage(1);
          }
        },
      }}
    />
  );

  return (
    <ControlTowerSectionCard
      zone="overdue"
      sectionId="ncr-capa-follow-up"
      title={
        <Space>
          <WarningOutlined />
          <span>
            NCR / CAPA / inspection follow-up ({openNcr} NCR · {overdueCapa} CAPA · {overdueInsp}{' '}
            insp.)
          </span>
        </Space>
      }
      extra={
        <Space>
          <Button type="link" size="small" onClick={() => navigate('/qms/ncrs')}>
            NCR register
          </Button>
          <Button type="link" size="small" onClick={() => navigate('/qms/capas')}>
            CAPA register
          </Button>
        </Space>
      }
    >
      <ControlTowerMetricStrip zone="metricsPayables" title="Quality risk">
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Open NCRs"
            count={openNcr}
            icon={<WarningOutlined />}
            urgency={
              summary.critical_ncr_count > 0 ? 'critical' : openNcr > 0 ? 'attention' : 'neutral'
            }
            footerLink={{
              label: 'Follow up',
              onClick: () => {
                setMode('open_ncr');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Overdue CAPAs"
            count={overdueCapa}
            icon={<ExceptionOutlined />}
            urgency={overdueCapa > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'Follow up',
              onClick: () => {
                setMode('overdue_capa');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Overdue inspections"
            count={overdueInsp}
            icon={<ClockCircleOutlined />}
            urgency={overdueInsp > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'Follow up',
              onClick: () => {
                setMode('overdue_inspection');
                setPage(1);
              },
            }}
          />
        </Col>
      </ControlTowerMetricStrip>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12, marginBottom: 12 }}
        message="Full follow-up queue (not capped at 15). Age filters apply to the active tab."
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder={
            mode === 'open_ncr'
              ? 'Search NCR / severity / status'
              : mode === 'overdue_capa'
                ? 'Search CAPA / status'
                : 'Search inspection / status'
          }
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setSearchApplied(v.trim());
            setPage(1);
          }}
        />
        <Select
          allowClear
          placeholder="Aging"
          style={{ width: 140 }}
          value={aging}
          onChange={(v) => {
            setAging(v);
            setPage(1);
          }}
          options={[
            { value: '0-7', label: `0–7 (${agingCounts?.['0-7'] ?? 0})` },
            { value: '8-14', label: `8–14 (${agingCounts?.['8-14'] ?? 0})` },
            { value: '15-30', label: `15–30 (${agingCounts?.['15-30'] ?? 0})` },
            { value: '30+', label: `30+ (${agingCounts?.['30+'] ?? 0})` },
          ]}
        />
        <Button size="small" onClick={() => refetch()} loading={isFetching}>
          Refresh queue
        </Button>
        <Text type="secondary">
          {total} item{total === 1 ? '' : 's'}
        </Text>
      </Space>

      <Tabs
        size="small"
        activeKey={mode}
        onChange={(k) => {
          setMode(k as NcrCapaQueueMode);
          setPage(1);
        }}
        items={[
          {
            key: 'open_ncr',
            label: `Open NCRs (${openNcr})`,
            children: mode === 'open_ncr' ? tableBody : null,
          },
          {
            key: 'overdue_capa',
            label: `Overdue CAPAs (${overdueCapa})`,
            children: mode === 'overdue_capa' ? tableBody : null,
          },
          {
            key: 'overdue_inspection',
            label: `Overdue inspections (${overdueInsp})`,
            children: mode === 'overdue_inspection' ? tableBody : null,
          },
        ]}
      />
    </ControlTowerSectionCard>
  );
};

export default QmsNcrCapaFollowUpZone;
